/**
 * "Outsiders" tab inside WhoHasAccessDialog (step 9b + management).
 *
 * Lists external tenants that hold share_grants rooted at or above
 * this folder, grouped by permission level. Mirrors the People &
 * teams tab's left-sidebar-permission-tabs pattern: each permission
 * is a sub-tab; tags appear under whichever tab matches their MAX
 * permission. Direct grants on THIS folder get a Remove button;
 * inherited grants are read-only here (manage them on the ancestor).
 *
 * Privacy view (doc § "Privacy view"): org name + anonymous member
 * count, never per-member enumeration.
 *
 * Add new outsider: button at the bottom opens the existing
 * ShareDialog (same component the folder context menu uses), with
 * this folder pre-filled.
 */
import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import {
  Building2, Crown, EyeOff, Loader2, Network,
  SquarePen, Eye, X, Plus, Share2, ChevronDown, Check, GripVertical,
} from 'lucide-react'
import { useConfirm, useToast, useTheme, getThemeClass, useIsDesktop, EmptyState } from '@papan/core-ui/ui'
import { useTranslation } from '@papan/i18n'
import { orgConnectionsApi, type FolderAccessRow } from '../client/index.js'
import { ShareDialog } from './ShareDialog.js'
import { errorText } from '@papan/api-client'

type Permission = 'admin' | 'readwrite' | 'readcopy' | 'hidden'
/** A sub-tab key: the real permission levels plus the synthetic "All
 *  Permissions" overview — mirrors the People & teams tab's `all` tab. */
type TabKey = 'all' | Permission

const TAB_ORDER: TabKey[] = ['all', 'admin', 'readwrite', 'readcopy', 'hidden']
const PERM_LABELS: Record<TabKey, string> = {
  all: 'All Permissions',
  admin: 'Manage & Edit',
  readwrite: 'Read & Write',
  readcopy: 'Read-only',
  hidden: 'No access',
}
const PERM_DESCRIPTIONS: Record<TabKey, string> = {
  all: 'Every organization with access to this folder, across all permission levels.',
  admin: 'Outsiders can rename, delete, and edit contents — including re-sharing with their own members.',
  readwrite: 'Outsiders can rename, delete, and edit contents. Cannot re-share with others.',
  readcopy: 'Outsiders can view and download a copy. Cannot edit.',
  hidden: 'No access. Drag an outsider here to hide a child they inherit (reversible — they keep the parent share), or to remove a share root.',
}
// No 'all' entry: the synthetic "All Permissions" tab is iconless (matches the
// People & teams tab — the icon would imply a permission tier it doesn't carry).
const PERM_ICONS: Record<Permission, typeof Crown> = {
  admin: Crown, readwrite: SquarePen, readcopy: Eye, hidden: EyeOff,
}

interface Props {
  /** Folder the host dialog is rendering. The Outsiders tab is only
   *  meaningful with a folder context (folder-scoped share_grants);
   *  if absent, renders an explanatory empty state. */
  folderId?: string
  /** Folder name for the ShareDialog header (passed through from the
   *  host WhoHasAccessDialog). */
  folderName?: string
  folderPath?: string
  /** Caller's own effective permission on the folder — forwarded to the
   *  ShareDialog so its picker can't offer a level above what the caller
   *  holds (effective access is min(your permission, what you grant)). */
  myPermission?: string
  /** Whether the host row is a folder. "No access" (hidden) is folder-only — for a
   *  file it isn't a drop target (you can't set a file to No access). */
  isFolder?: boolean
  /** Rendered above the right pane (the host's People & teams / Outsiders
   *  view switch) — sits over the right section only, not the sidebar. */
  topSlot?: ReactNode
  /** Hosted on a personal drive: "outside" means outside this drive, not an
   *  organization — the empty-state copy says so. */
  personal?: boolean
}

function normalizePerm(p: string): Permission {
  // share_grants stores permission as text; coerce unknown values to
  // 'readcopy' (the lowest "user can still see something" tier — the
  // standalone Read-only tier was collapsed into readcopy, migration
  // 0061). Stale 'read' values from before the migration land here.
  if (p === 'read') return 'readcopy'
  return (['admin','readwrite','readcopy','hidden'] as const)
    .includes(p as Permission)
    ? (p as Permission)
    : 'readcopy'
}

export function OutsiderTab({ folderId, folderName, folderPath, myPermission, isFolder = true, topSlot, personal = false }: Props) {
  const { t } = useTranslation('files')
  const toast = useToast()
  const confirm = useConfirm()
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)
  const [rows, setRows] = useState<FolderAccessRow[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const isDesktop = useIsDesktop()
  const [activeTabRaw, setActiveTab] = useState<TabKey>('all')
  /** PHONE: pinned to `all`, exactly as WhoHasAccessDialog's People view is and
   *  for the same reason — this tab is that view's sibling and rendered its
   *  DESKTOP two-column layout at 390px, a 192px sidebar beside a pane whose
   *  copy wrapped two words to the line. The `all` branch below already groups
   *  by tier, so the sidebar filters a list that describes itself. */
  const activeTab: TabKey = isDesktop ? activeTabRaw : 'all'
  const [shareOpen, setShareOpen] = useState(false)
  // Drag-to-tab (change permission) + per-card re-sharing dropdown, mirroring
  // the People & teams tab.
  const [dragTenantId, setDragTenantId] = useState<string | null>(null)
  const [dragOverTab, setDragOverTab] = useState<TabKey | null>(null)
  // Re-sharing menu: which card's menu is open + where to portal it. Fixed
  // viewport coords from the trigger so the menu isn't clipped by the dialog's
  // nested overflow containers, nor drawn over the card's own avatar/name.
  const [sharingMenu, setSharingMenu] = useState<{ tenantId: string; top: number; left: number } | null>(null)
  const [busyTenantId, setBusyTenantId] = useState<string | null>(null)
  // Mid-drag highlight for the body dropzone (drop anywhere in the active tier).
  const [bodyHover, setBodyHover] = useState(false)
  // Which All-Permissions section (right panel) the drag is currently over.
  const [dropSection, setDropSection] = useState<Permission | null>(null)

  const load = useCallback(async () => {
    if (!folderId) return
    setError(null)
    try {
      const r = await orgConnectionsApi.listFolderAccess(folderId)
      setRows(r.folderAccess)
    } catch (e) {
      const text = errorText(e, 'Failed to load')   // #1316: a dropped answer shows nothing
      if (text !== null) setError(text)
    }
  }, [folderId])

  useEffect(() => { void load() }, [load])

  // Close the re-sharing dropdown on any outside click.
  useEffect(() => {
    if (!sharingMenu) return
    const onDoc = (e: MouseEvent) => {
      if (!(e.target as HTMLElement).closest('[data-sharing-menu]')) setSharingMenu(null)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [sharingMenu])

  const handleRemove = async (row: FolderAccessRow) => {
    if (!folderId) return
    const ok = await confirm({
      title: `Remove ${row.name}'s access?`,
      message: (
        <span>
          Revoke this folder's direct grant to <strong>{row.name}</strong>?
          Inherited grants on ancestor folders (if any) are NOT touched.
          The existing <code>.shared</code> file in their tree becomes
          a broken pointer (no content); they can delete it themselves.
        </span>
      ),
      type: 'danger',
      confirmText: 'Remove access',
    })
    if (!ok) return
    try {
      const r = await orgConnectionsApi.revokeFolderAccess(folderId, row.tenantId)
      if (r.revoked > 0) {
        toast.success(`Removed ${row.name}'s access`)
      } else {
        toast.warning('No direct grant found to remove (grant is inherited)')
      }
      void load()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to remove')
    }
  }

  // Drop targets for an outsider card. readcopy | readwrite change the grant level.
  // 'admin' (Manage & Edit) is NOT offered to outsiders — full management of your
  // content by an external org is out of scope. 'hidden' (No access) is folder-only —
  // a FILE can't be set to No access (it inherits from its parent), so it's a drop
  // target only for folders; share root ⇒ revoke, inherited child ⇒ reversible hidden cap.
  const DROPPABLE: Permission[] = isFolder
    ? ['readwrite', 'readcopy', 'hidden']
    : ['readwrite', 'readcopy']

  const changePermission = async (row: FolderAccessRow, perm: Permission) => {
    if (!folderId) return
    // No-op if the card is already in this tier (its nearest-grant level).
    if (perm === normalizePerm(row.nearestPermission ?? row.maxPermission)) return
    // "No access" on the share ROOT (the org's access originates here — no
    // inherited grant above) = revoke the whole share. handleRemove owns its
    // own confirm + toast.
    if (perm === 'hidden' && !row.hasInheritedAccess) return handleRemove(row)
    setBusyTenantId(row.tenantId)
    try {
      if (perm === 'hidden') {
        // Inherited child: hide just this folder (reversible) while they keep the
        // parent — nearest-wins makes the child resolve hidden.
        await orgConnectionsApi.updateFolderAccess(folderId, row.tenantId, { permission: 'hidden' })
        toast.success(`Hid this folder from ${row.name}`)
      } else {
        // readcopy / readwrite — upsert a grant on THIS folder (raise, lower, or
        // un-hide a child; edit the root in place). The PATCH creates the child grant
        // when it's only inherited, so the change always sticks.
        await orgConnectionsApi.updateFolderAccess(folderId, row.tenantId, { permission: perm as 'readcopy' | 'readwrite' })
        toast.success(`Updated ${row.name}'s access`)
      }
      await load()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to update access')
    } finally {
      setBusyTenantId(null)
    }
  }

  const changeSharing = async (row: FolderAccessRow, allow: boolean) => {
    if (!folderId) return
    setSharingMenu(null)
    if (allow === row.allowSharing) return
    setBusyTenantId(row.tenantId)
    try {
      await orgConnectionsApi.updateFolderAccess(folderId, row.tenantId, { allowSharing: allow })
      toast.success(`${row.name} can ${allow ? 'now re-share' : 'no longer re-share'}`)
      await load()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to update re-sharing')
    } finally {
      setBusyTenantId(null)
    }
  }

  // ─── Loading / error / no-folder states ─────────────────────────────

  if (!folderId) {
    return (
      <div className="rounded-md border themed-border p-6 text-center">
        <Network size={24} className="mx-auto themed-icon-active opacity-50" />
        <p className="text-sm themed-text-muted mt-3">No folder context.</p>
        <p className="text-xs themed-text-muted mt-1">
          Open the Outsiders tab on a specific folder to manage external access.
        </p>
      </div>
    )
  }

  if (error) {
    return <p className="text-xs text-red-400 p-3">{error}</p>
  }
  if (rows === null) {
    return (
      <div className="flex items-center gap-2 themed-text-muted text-sm p-3">
        <Loader2 size={14} className="animate-spin" /> Loading…
      </div>
    )
  }

  // Bucket by permission.
  const byPerm: Record<Permission, FolderAccessRow[]> = {
    admin: [], readwrite: [], readcopy: [], hidden: [],
  }
  // Bucket by the NEAREST-explicit grant (mirrors the resolver): a child raised or
  // lowered relative to its parent — and a hidden child — shows in its own tier,
  // not its inherited maxPermission.
  rows.forEach(r => { byPerm[normalizePerm(r.nearestPermission ?? r.maxPermission)].push(r) })

  // One outsider pill card — shared by the per-level grid and the grouped
  // "All Permissions" sections. Direct grants are draggable to a sidebar tab
  // to change level.
  const renderCard = (r: FolderAccessRow): ReactNode => (
    <div
      key={r.tenantId}
      /* Pill card matching WhoHasAccessDialog's People & teams tag
         (h-14 with 3-line text column). Direct grants are draggable
         to a permission tab (change level) — `group` enables the
         hover-only X + drag handle. */
      draggable={r.directGrant || r.hasInheritedAccess}
      onDragStart={() => (r.directGrant || r.hasInheritedAccess) && setDragTenantId(r.tenantId)}
      onDragEnd={() => { setDragTenantId(null); setDragOverTab(null); setDropSection(null) }}
      className={`group relative flex items-center gap-2 h-14 pl-2.5 pr-2 rounded-full themed-bg-tertiary hover:themed-bg-secondary transition-colors select-none ${
        (r.directGrant || r.hasInheritedAccess) ? 'cursor-grab active:cursor-grabbing' : ''
      } ${busyTenantId === r.tenantId ? 'opacity-50' : ''}`}
    >
      {/* Avatar (emerald distinguishes orgs from team-blue and role-amber) */}
      <div className="w-10 h-10 rounded-full bg-emerald-600 flex items-center justify-center shrink-0">
        <Building2 size={14} className="text-white" />
      </div>
      <div className="flex-1 min-w-0">
        {/* Primary row: org name */}
        <div className="text-xs leading-tight themed-text truncate">
          {r.name}
        </div>
        {/* Subline: when access began + inherited pill, OR the
            reshare markers (provenance for the owner, "reshared by
            you" for the resharer). */}
        <div className="text-[10px] themed-text-muted leading-tight truncate flex items-center gap-1.5">
          {r.reshareByYou ? (
            <span className="opacity-80">
              Reshared by you · {r.reshareState === 'approved' ? 'approved by owner' : 'awaiting owner approval'}
            </span>
          ) : (
            <>
              <span className="opacity-80">Since {formatDate(r.firstSharedAt)}</span>
              {r.reshareVia && (
                <span className="opacity-80 truncate">· via {r.reshareVia}</span>
              )}
              {!r.directGrant && (
                <span className="px-1 py-px rounded-sm themed-bg-secondary text-[9px] uppercase tracking-wide opacity-80">
                  inherited
                </span>
              )}
            </>
          )}
        </div>
        {/* Re-sharing dropdown (direct grants only). The menu is
            portaled to <body> at fixed viewport coords taken from the
            trigger — anchoring it inside this h-14 pill made it open
            over the card's own avatar/name and risked clipping by the
            dialog's nested overflow containers. */}
        {r.directGrant && (
          <div className="leading-tight" data-sharing-menu>
            <button
              onClick={(e) => {
                const rect = e.currentTarget.getBoundingClientRect()
                setSharingMenu(prev => prev?.tenantId === r.tenantId
                  ? null
                  : { tenantId: r.tenantId, top: rect.bottom + 4, left: Math.min(rect.left, window.innerWidth - 152) })
              }}
              className="flex items-center gap-1 text-[10px] themed-text-muted hover:themed-text transition-colors"
            >
              <Share2 size={9} className="opacity-70" />
              {r.allowSharing ? 'Allow sharing' : 'No sharing'}
              <ChevronDown size={9} />
            </button>
            {sharingMenu?.tenantId === r.tenantId && createPortal(
              <div
                data-sharing-menu
                className={`fixed z-[320] ${themeClass} w-36 rounded-md border themed-border themed-bg-primary shadow-xl py-0.5`}
                style={{ top: sharingMenu.top, left: sharingMenu.left }}
              >
                {[{ v: true, label: 'Allow sharing' }, { v: false, label: 'No sharing' }].map(opt => (
                  <button
                    key={opt.label}
                    onClick={() => void changeSharing(r, opt.v)}
                    className={`w-full px-2 py-1 text-left text-[11px] flex items-center gap-1.5 themed-bg-hover transition-colors ${
                      r.allowSharing === opt.v ? 'text-blue-400 font-medium' : 'themed-text'
                    }`}
                  >
                    <Check size={10} className={`shrink-0 ${r.allowSharing === opt.v ? 'opacity-100' : 'opacity-0'}`} />
                    {opt.label}
                  </button>
                ))}
              </div>,
              document.body,
            )}
          </div>
        )}
      </div>
      {r.directGrant && !r.directHidden && (
        <>
          <GripVertical size={12} className="themed-text-muted opacity-0 group-hover:opacity-40 shrink-0" />
          <button
            onClick={() => void handleRemove(r)}
            className="p-0.5 rounded-full opacity-50 hover:opacity-100 hover:bg-current/10 transition-opacity cursor-pointer shrink-0"
            aria-label={`Remove ${r.name}'s access`}
          >
            <X size={12} />
          </button>
        </>
      )}
    </div>
  )

  const addTile = (
    <button
      onClick={() => setShareOpen(true)}
      className="w-full h-14 flex items-center justify-center gap-1.5 rounded-full border border-dashed themed-border-secondary themed-text-muted hover:themed-text hover:themed-bg-tertiary transition-colors text-xs"
    >
      <Plus size={14} className="shrink-0" />
      <span>Share with new outsider</span>
    </button>
  )

  return (
    // Spacing must match WhoHasAccessDialog's People view (nav has mr-2, no
    // extra gap) so the right section doesn't change width when toggling.
    <div className="flex flex-col md:flex-row flex-1 min-h-0 md:min-h-[400px]">
      {/* ─── Left sidebar: permission tabs ───────────────────────────
          Width + child-tab indent must match WhoHasAccessDialog's
          People & teams sidebar so the right pane doesn't shift x
          when toggling between the two views. */}
      <nav className="w-48 shrink-0 hidden md:flex flex-col gap-0.5 pr-2 py-2 mr-2">
        <div className="text-[10px] uppercase tracking-wider themed-text font-medium py-2 mb-1 ml-3">
          Permission
        </div>
        {TAB_ORDER.map(p => {
          const count = p === 'all' ? (rows?.length ?? 0) : byPerm[p].length
          const isActive = activeTab === p
          const isAllTab = p === 'all'
          // 'all' is a synthetic, non-tier tab — iconless, like People & teams.
          const Icon = isAllTab ? null : PERM_ICONS[p as Permission]
          const canDrop = p !== 'all' && dragTenantId != null && DROPPABLE.includes(p)
          const dragOverThis = canDrop && dragOverTab === p
          // Manage & Edit / No access aren't grantable to outsiders. When
          // empty (the normal case) dim them and block selection so it's
          // obvious they're not drop targets. If a legacy grant somehow sits
          // there (count>0), keep the tab usable so it can be viewed/moved.
          // The synthetic "All Permissions" tab is always selectable.
          const notForOutsiders = p !== 'all' && !DROPPABLE.includes(p) && count === 0
          return (
            <button
              key={p}
              onClick={() => { if (!notForOutsiders) setActiveTab(p) }}
              aria-disabled={notForOutsiders || undefined}
              title={notForOutsiders ? (p === 'hidden' ? "No access is folder-only — a file can’t be set to No access" : "Manage & Edit isn’t available for outsiders") : undefined}
              onDragOver={(e) => {
                if (!canDrop) { if (dragTenantId != null) e.dataTransfer.dropEffect = 'none'; return }
                e.preventDefault()
                e.dataTransfer.dropEffect = 'move'
                if (dragOverTab !== p) setDragOverTab(p)
              }}
              onDragLeave={() => { if (dragOverTab === p) setDragOverTab(null) }}
              onDrop={(e) => {
                e.preventDefault()
                const id = dragTenantId
                setDragTenantId(null); setDragOverTab(null)
                if (!canDrop || !id) return
                const row = (rows ?? []).find(r => r.tenantId === id)
                if (row) { setActiveTab(p); void changePermission(row, p as Permission) }
              }}
              className={`
                ${isAllTab ? 'w-full' : 'w-[calc(100%-0.5rem)] ml-2'} text-left px-3 py-2 text-sm rounded-full
                transition-colors flex items-center gap-2 justify-between
                ${notForOutsiders
                  ? 'themed-text-muted opacity-40 cursor-not-allowed'
                  : dragOverThis
                  ? 'bg-brand-500/25 themed-text font-medium'
                  : isActive
                  ? 'themed-bg-primary themed-text font-medium'
                  : 'themed-text-muted hover:themed-bg-primary'}
              `}
            >
              <span className="flex items-center gap-2 min-w-0">
                {Icon && <Icon size={14} className="shrink-0" />}
                <span className="truncate">{PERM_LABELS[p]}</span>
              </span>
              {count > 0 && (
                <span className={`text-[11px] font-medium tabular-nums min-w-[20px] inline-flex items-center justify-center px-1.5 h-5 rounded-full ${isActive ? 'themed-bg-tertiary themed-text' : 'themed-bg-tertiary themed-text-muted'}`}>
                  {count}
                </span>
              )}
            </button>
          )
        })}
      </nav>

      {/* ─── Right pane: header + grid of pill cards ─────────────────
          Matches WhoHasAccessDialog's People & teams pane shape:
          rounded-2xl bordered container with themed-bg-primary, a
          header bar with the active permission icon + description,
          and a 2-column grid of tags inside the scrollable body. */}
      {/* Right column — view switch (above the box) + the bordered pane. */}
      <div className="flex-1 flex flex-col min-w-0 min-h-0">
        {topSlot}
      <div className={`flex-1 flex flex-col min-h-0 overflow-hidden ${
        isDesktop ? 'themed-bg-primary border themed-border rounded-2xl' : ''
      }`}>
        {/* Header bar */}
        {isDesktop && (
        <div className="px-5 py-3 border-b themed-border-secondary themed-bg-primary">
          <p className="text-xs themed-text-muted flex items-start gap-2">
            {(() => {
              const ActiveIcon = activeTab === 'all' ? null : PERM_ICONS[activeTab]
              return ActiveIcon ? <ActiveIcon size={14} className="shrink-0 mt-[1px] themed-text" /> : null
            })()}
            <span>
              <span className="font-medium themed-text">{PERM_LABELS[activeTab]}</span>
              <span> — {PERM_DESCRIPTIONS[activeTab]}</span>
            </span>
          </p>
        </div>
        )}

        {/* Body — scrollable, 2-column grid of pill cards + Add tile. */}
        <div className="flex-1 overflow-y-auto px-4 md:px-5 pt-3 pb-4 md:py-4 flex flex-col">
          {activeTab === 'all' ? (
            /* All Permissions: one grouped section per tier (icon + uppercase
               label + count, then the card grid) — matches WhoHasAccessDialog's
               People & teams audit view. */
            (rows?.length ?? 0) === 0 ? (
              /* NOTHING SHARED OUT AT ALL — say what the view is for.
                 The tier sections below render unconditionally so they can act
                 as drop targets, which on an empty folder meant two headings
                 reading "READ & WRITE 0" and "READ-ONLY 0" above a dashed
                 button: a count of nothing, twice, explaining neither what an
                 outsider is nor why the list is bare. A folder that has never
                 been shared outside the organisation is the NORMAL state, not
                 a failure, and the empty view should read as a starting point.
                 The drop targets are not lost with them — there is nothing to
                 drag when there are no cards. */
              <EmptyState
                icon={Network}
                title={t(personal ? 'outsiderTab.empty.titleDrive' : 'outsiderTab.empty.titleOrg')}
                body={t(`outsiderTab.empty.body${personal ? 'Drive' : 'Org'}${isFolder ? 'Folder' : 'File'}`)}
                action={addTile}
              />
            ) : (
            <div key="all-view" className="flex flex-col gap-6">
              {TAB_ORDER
                .filter((p): p is Permission => p !== 'all')
                // Only tiers grantable to outsiders (Read & Write, Read-only).
                // Manage & Edit / No access are greyed-out sidebar tabs, so omit
                // them here — unless a legacy grant happens to sit there.
                .filter(level => DROPPABLE.includes(level) || byPerm[level].length > 0)
                .map(level => {
                const Icon = PERM_ICONS[level]
                const cards = byPerm[level]
                // Each section is its OWN drop target (matches People & teams): drag
                // a card onto a section to assign that tier. The dashed border lights
                // up for droppable tiers mid-drag; Manage & Edit dims (not droppable).
                const canDropHere = DROPPABLE.includes(level) && dragTenantId != null
                const dragHere = canDropHere && dropSection === level
                return (
                  <section
                    key={level}
                    onDragOver={(e) => {
                      if (!canDropHere) { if (dragTenantId != null) e.dataTransfer.dropEffect = 'none'; return }
                      e.preventDefault()
                      e.dataTransfer.dropEffect = 'move'
                      if (dropSection !== level) setDropSection(level)
                    }}
                    onDragLeave={(e) => {
                      if (!e.currentTarget.contains(e.relatedTarget as Node | null) && dropSection === level) setDropSection(null)
                    }}
                    onDrop={(e) => {
                      e.preventDefault()
                      const dropped = dragTenantId
                      setDragTenantId(null); setDropSection(null)
                      if (!canDropHere || dropped == null) return
                      const row = (rows ?? []).find(r => r.tenantId === dropped)
                      if (row) void changePermission(row, level)
                    }}
                    className={`rounded-md p-2 pt-1 pb-3 -m-2 border-2 border-dashed transition-colors ${
                      dragTenantId != null
                        ? dragHere
                          ? 'border-brand-500 bg-brand-500/10'
                          : DROPPABLE.includes(level)
                            ? 'border-brand-500/30'
                            : 'border-transparent opacity-50'
                        : 'border-transparent'
                    }`}
                  >
                    <div className="flex items-center gap-1.5 mb-2">
                      <Icon size={12} className="themed-text" />
                      <span className="text-[11px] uppercase tracking-wider themed-text font-medium">{PERM_LABELS[level]}</span>
                      <span className="text-[10px] themed-text-muted ml-1">{cards.length}</span>
                    </div>
                    {cards.length > 0 && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">{cards.map(renderCard)}</div>
                    )}
                  </section>
                )
              })}
              {addTile}
            </div>
            )
          ) : (
            /* A single tier: a dashed body dropzone (lights mid-drag for a
               droppable tier so the whole pane is a drop target, like People &
               teams) wrapping the 2-col card grid + the Add tile.
               `key` distinct from the all-view above so React MOUNTS a fresh node
               on the all↔tier switch instead of reusing the all-view's <div>:
               reuse made `transition-colors` animate this border from the
               inherited near-white default to transparent — a white-border blink. */
            <div
              key="tier-view"
              onDragOver={(e) => {
                if (dragTenantId == null || !DROPPABLE.includes(activeTab as Permission)) return
                e.preventDefault(); e.dataTransfer.dropEffect = 'move'
                if (!bodyHover) setBodyHover(true)
              }}
              onDragLeave={() => setBodyHover(false)}
              onDrop={(e) => {
                e.preventDefault(); setBodyHover(false)
                const id = dragTenantId
                setDragTenantId(null); setDragOverTab(null)
                if (id == null || !DROPPABLE.includes(activeTab as Permission)) return
                const row = (rows ?? []).find(r => r.tenantId === id)
                if (row) void changePermission(row, activeTab as Permission)
              }}
              className={`flex-1 rounded-md p-2 -m-2 border-2 border-dashed transition-colors ${
                dragTenantId != null && DROPPABLE.includes(activeTab as Permission)
                  ? bodyHover ? 'border-brand-500 bg-brand-500/10' : 'border-brand-500/40 bg-brand-500/5'
                  : 'border-transparent'
              }`}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {byPerm[activeTab].map(renderCard)}
                {addTile}
              </div>
            </div>
          )}
        </div>
      </div>
      </div>

      {shareOpen && folderId && folderName && (
        <ShareDialog
          folderId={folderId}
          folderName={folderName}
          folderPath={folderPath}
          myPermission={myPermission}
          onClose={() => {
            setShareOpen(false)
            void load()  // Refresh after the share request lands.
          }}
        />
      )}
    </div>
  )
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  } catch {
    return iso
  }
}
